import { HttpError } from '@littlebird/auth-client';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { type AgentInboxContact } from '@/backendApis';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { IconButton } from '@/components/ui/IconButton';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/Switch';
import TextArea from '@/components/ui/TextArea';

import { AgentInboxDialogHeader } from './agent-inbox-dialog-header';
import {
  useAgentInboxContactsQuery,
  useRemoveAgentInboxContactMutation,
  useSaveAgentInboxContactMutation,
} from './agent-inbox.query';

export const AgentInboxContacts = ({ onBack }: { onBack: () => void }) => {
  const contacts = useAgentInboxContactsQuery();
  const add = useSaveAgentInboxContactMutation();
  const [draft, setDraft] = useState('');
  const candidate = draft.trim().toLowerCase();
  const isDuplicate = !!contacts.data?.some(
    (contact) => contact.address === candidate
  );

  return (
    <>
      <AgentInboxDialogHeader
        title="Contacts"
        description="When someone here replies on a thread you copied your assistant on, it answers them directly. Turn trust off to approve its draft first."
        onBack={onBack}
      />
      <YStack className="min-h-0 flex-1 gap-5 px-6 pb-6">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!contacts.data || !candidate || isDuplicate) return;
            add.mutate(
              { address: candidate, trusted: true, instructions: '' },
              { onSuccess: () => setDraft('') }
            );
          }}
        >
          <YStack className="gap-2">
            <XStack className="gap-2">
              <Input
                aria-label="Email or domain"
                placeholder="russ@acme.com or @acme.com"
                spellCheck={false}
                autoComplete="off"
                className="h-9"
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  add.reset();
                }}
              />
              <Button
                type="submit"
                disabled={!contacts.data || !candidate || isDuplicate}
                isLoading={add.isPending}
              >
                Add
              </Button>
            </XStack>
            {add.isError ? (
              <p role="alert" className="text-xs text-danger-11">
                {userErrorMessage(add.error) ?? "Couldn't add that contact."}
              </p>
            ) : (
              <p className="text-xs text-foreground-muted">
                {isDuplicate
                  ? 'Already in your contacts.'
                  : 'Add an email, or @acme.com for everyone there.'}
              </p>
            )}
          </YStack>
        </form>
        <ContactList
          contacts={contacts.data}
          isError={contacts.isError}
          onRetry={() => void contacts.refetch()}
        />
      </YStack>
    </>
  );
};

type ContactListProps = {
  contacts: AgentInboxContact[] | undefined;
  isError: boolean;
  onRetry: () => void;
};

const ContactList = ({ contacts, isError, onRetry }: ContactListProps) => {
  if (isError) {
    return (
      <YStack center className="flex-1 gap-3 text-center">
        <p role="alert" className="text-sm text-danger-11">
          Couldn&apos;t load your contacts.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </YStack>
    );
  }

  if (!contacts) {
    return <Skeleton aria-busy className="h-32 w-full rounded-xl" />;
  }

  if (contacts.length === 0) {
    return (
      <Empty.Root variant="compact" className="flex-1">
        <Empty.Media variant="icon">
          <Icons.users />
        </Empty.Media>
        <Empty.Header>
          <Empty.Title>No contacts yet</Empty.Title>
          <Empty.Description>
            Add the people your assistant emails with. You can turn trust off
            for anyone.
          </Empty.Description>
        </Empty.Header>
      </Empty.Root>
    );
  }

  return (
    <ScrollArea className="min-h-0 flex-1">
      <YStack className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
        {contacts.map((contact) => (
          <ContactRow key={contact.address} contact={contact} />
        ))}
      </YStack>
    </ScrollArea>
  );
};

const ContactAvatar = ({ address }: { address: string }) =>
  address.startsWith('@') ? (
    <XStack
      center
      aria-hidden
      className="size-8 shrink-0 rounded-full bg-surface-hover text-foreground-muted"
    >
      <Icons.building className="size-4" />
    </XStack>
  ) : (
    <InitialsAvatar
      name={address}
      seed={address}
      className="size-8 shrink-0 text-[0.6875rem]"
    />
  );

const userErrorMessage = (error: Error | null) => {
  const message: unknown =
    error instanceof HttpError
      ? error.body?.detail?.user_error_message
      : undefined;
  return typeof message === 'string' ? message : undefined;
};

const whatHappens = (address: string, trusted: boolean) => {
  const reply = trusted
    ? 'replies go straight to them'
    : 'you approve replies first';
  return address.startsWith('@')
    ? `Everyone at ${address.slice(1)} · ${reply}`
    : `${reply.charAt(0).toUpperCase()}${reply.slice(1)}`;
};

const ContactRow = ({ contact }: { contact: AgentInboxContact }) => {
  const save = useSaveAgentInboxContactMutation();
  const remove = useRemoveAgentInboxContactMutation();
  const [instructions, setInstructions] = useState(contact.instructions);
  const [isEditing, setIsEditing] = useState(false);
  const trusted = save.isPending ? save.variables.trusted : contact.trusted;
  const preview = instructions.trim();

  return (
    <YStack className="group gap-1.5 px-4 py-3">
      <XStack className="items-center gap-3">
        <ContactAvatar address={contact.address} />
        <YStack className="min-w-0 flex-1 gap-0.5">
          <p className="truncate text-sm font-medium text-foreground">
            {contact.address}
          </p>
          <p className="truncate text-sm text-foreground-muted">
            {whatHappens(contact.address, trusted)}
          </p>
        </YStack>
        <IconButton
          icon={Icons.trash}
          size="sm"
          variant="ghost"
          tooltip="Remove"
          aria-label={`Remove ${contact.address}`}
          isLoading={remove.isPending}
          onClick={() => remove.mutate(contact.address)}
          className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        />
        <Switch
          aria-label={`Trust ${contact.address}`}
          checked={trusted}
          onCheckedChange={(checked) =>
            save.mutate({
              address: contact.address,
              trusted: checked,
              instructions,
            })
          }
        />
      </XStack>
      <YStack className="pl-11">
        {isEditing ? (
          <TextArea
            autoFocus
            aria-label={`Instructions for ${contact.address}`}
            placeholder="How should your assistant handle them?"
            maxLength={2000}
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            onBlur={() => {
              setIsEditing(false);
              if (preview === contact.instructions) return;
              save.mutate({ address: contact.address, trusted, instructions });
            }}
            className="max-h-40 resize-none text-sm [field-sizing:content]"
          />
        ) : (
          <Button
            variant="ghost"
            size="xs"
            aria-label={`${preview ? 'Edit' : 'Add'} instructions for ${contact.address}`}
            onClick={() => setIsEditing(true)}
            className="-ml-2 w-fit max-w-full justify-start gap-1.5 text-foreground-muted"
          >
            {preview ? (
              <Icons.notepad className="size-3.5 shrink-0" />
            ) : (
              <Icons.plus className="size-3.5 shrink-0" />
            )}
            <span className="truncate">{preview || 'Add instructions'}</span>
          </Button>
        )}
      </YStack>
      {(save.isError || remove.isError) && (
        <p role="alert" className="pl-11 text-xs text-danger-11">
          Couldn&apos;t update this contact. Try again.
        </p>
      )}
    </YStack>
  );
};
